/**
 * Configurations pour les pages d'administration des assets
 */

import type { User } from '@/api/types/core/users';
import type { Agent } from '@/api/types/entities/agent/types';
import type { DigitalEntity } from '@/api/types/entities/digitalEntity/types';
import type { Location } from '@/api/types/entities/location/types';
import type { MaterialEntity } from '@/api/types/entities/materialEntity/types';
import type { Project } from '@/api/types/entities/project/types';
import type { Memo } from '@/api/types/memos/types';

export interface ColumnConfig {
  key: string;
  label: string;
  type: 'text' | 'number' | 'select' | 'checkbox' | 'date' | 'readonly' | 'textarea';
  editable?: boolean;
  superadminOnly?: boolean;
  sortable?: boolean;
  width?: string;
  options?: { value: unknown; label: string }[];
  formatter?: (value: unknown) => string;
  required?: boolean;
  searchable?: boolean;
}

export interface AdminAssetConfig {
  title: string;
  entityType: 'agents' | 'digitalEntities' | 'materialEntities' | 'locations' | 'projects' | 'memos' | 'users';
  detailRoute: string;
  backRoute: string;
  columns: ColumnConfig[];
  searchableFields: string[];
  requiredFields: string[];
  exportFilename: string;
}

export const ADMIN_ASSET_CONFIGS: Record<string, AdminAssetConfig> = {
  agents: {
    title: 'Agents',
    entityType: 'agents',
    detailRoute: '/agent',
    backRoute: '/admin',
    exportFilename: 'agents',
    searchableFields: ['first_name', 'last_name', 'roles', 'group', 'affiliation', 'idref_id', 'affiliation_id'],
    requiredFields: ['user_id'],
    columns: [
      { 
        key: 'user_id', 
        label: 'Utilisateur', 
        type: 'select', 
        editable: true, 
        sortable: true, 
        required: true,
        searchable: false,
        width: 'w-40'
      },
      { 
        key: 'first_name', 
        label: 'Prénom', 
        type: 'text', 
        editable: true, 
        sortable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'last_name', 
        label: 'Nom', 
        type: 'text', 
        editable: true, 
        sortable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'is_legal_entity', 
        label: 'Type', 
        type: 'select', 
        editable: true,
        sortable: true,
        searchable: false,
        width: 'w-28',
        options: [
          { value: false, label: 'Physique' }, 
          { value: true, label: 'Morale' }
        ],
        formatter: (value: unknown): string => value ? 'Morale' : 'Physique'
      },
      { 
        key: 'idref_id', 
        label: 'ID Ref', 
        type: 'text', 
        editable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'roles', 
        label: 'Rôles', 
        type: 'text', 
        editable: true,
        searchable: true,
        width: 'w-40'
      },
      { 
        key: 'group', 
        label: 'Groupe', 
        type: 'text', 
        editable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'affiliation', 
        label: 'Affiliation', 
        type: 'text', 
        editable: true,
        searchable: true,
        width: 'w-40'
      },
      { 
        key: 'affiliation_id', 
        label: 'ID Affiliation', 
        type: 'text', 
        editable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'is_public', 
        label: 'Public', 
        type: 'checkbox', 
        editable: true,
        sortable: true,
        searchable: false,
        width: 'w-20'
      },
      { 
        key: 'created_at', 
        label: 'Créé le', 
        type: 'date', 
        sortable: true,
        editable: false,
        searchable: false,
        width: 'w-40'
      },
      { 
        key: 'updated_at', 
        label: 'Modifié le', 
        type: 'date', 
        sortable: true,
        editable: false,
        searchable: false,
        width: 'w-40'
      }
    ]
  },

  digitalEntities: {
    title: 'Entités Numériques',
    entityType: 'digitalEntities',
    detailRoute: '/digital-entity',
    backRoute: '/admin',
    exportFilename: 'entites_numeriques',
    searchableFields: ['appellation', 'description', 'type', 'format', 'url', 'owner', 'user'],
    requiredFields: ['user_id', 'appellation'],
    columns: [
      { 
        key: 'user_id', 
        label: 'Utilisateur', 
        type: 'select', 
        editable: true, 
        sortable: true, 
        required: true,
        searchable: false,
        width: 'w-40'
      },
      { 
        key: 'appellation', 
        label: 'Appellation', 
        type: 'text', 
        editable: true, 
        sortable: true,
        required: true,
        searchable: true,
        width: 'w-48'
      },
      { 
        key: 'description', 
        label: 'Description', 
        type: 'textarea', 
        editable: true,
        searchable: true,
        width: 'w-64'
      },
      { 
        key: 'type', 
        label: 'Type', 
        type: 'text', 
        editable: true,
        sortable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'format', 
        label: 'Format', 
        type: 'text', 
        editable: true,
        sortable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'url', 
        label: 'URL', 
        type: 'text', 
        editable: true,
        searchable: true,
        width: 'w-48'
      },
      { 
        key: 'version', 
        label: 'Version', 
        type: 'text', 
        editable: true,
        width: 'w-24'
      },
      { 
        key: 'copyright', 
        label: 'Copyright', 
        type: 'text', 
        editable: true,
        width: 'w-32'
      },
      { 
        key: 'de_link', 
        label: 'Lien', 
        type: 'text', 
        editable: true,
        sortable: true,
        width: 'w-32'
      },
      { 
        key: 'file_spec', 
        label: 'Spécifications fichier', 
        type: 'text', 
        editable: true,
        sortable: true,
        width: 'w-40'
      },
      { 
        key: 'owner', 
        label: 'Propriétaire', 
        type: 'text', 
        editable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'user', 
        label: 'Utilisateur créateur', 
        type: 'text', 
        editable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'is_public', 
        label: 'Public', 
        type: 'checkbox', 
        editable: true,
        sortable: true,
        width: 'w-20'
      },
      { 
        key: 'created_at', 
        label: 'Créé le', 
        type: 'date', 
        sortable: true,
        editable: false,
        width: 'w-40'
      },
      { 
        key: 'updated_at', 
        label: 'Modifié le', 
        type: 'date', 
        sortable: true,
        editable: false,
        width: 'w-40'
      }
    ]
  },

  materialEntities: {
    title: 'Entités Matérielles',
    entityType: 'materialEntities',
    detailRoute: '/material-entity',
    backRoute: '/admin',
    exportFilename: 'entites_materielles',
    searchableFields: ['appellation', 'identifier', 'type', 'material'],
    requiredFields: ['user_id', 'appellation'],
    columns: [
      { 
        key: 'user_id', 
        label: 'Utilisateur', 
        type: 'select', 
        editable: true, 
        sortable: true, 
        required: true,
        width: 'w-40'
      },
      { 
        key: 'appellation', 
        label: 'Appellation', 
        type: 'text', 
        editable: true, 
        sortable: true,
        required: true,
        searchable: true,
        width: 'w-48'
      },
      { 
        key: 'identifier', 
        label: 'Identifiant', 
        type: 'text', 
        editable: true,
        sortable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'type', 
        label: 'Type', 
        type: 'text', 
        editable: true,
        sortable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'material', 
        label: 'Matériau', 
        type: 'text', 
        editable: true,
        sortable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'provenance_location', 
        label: 'Localisation', 
        type: 'readonly', 
        editable: false,
        width: 'w-40',
        formatter: (value: unknown): string => {
          if (value && typeof value === 'object' && 'site' in value) {
            return (value as { site: string }).site;
          }
          return 'Non définie';
        }
      },
      { 
        key: 'provenance_interior_exterior', 
        label: 'Int/Ext', 
        type: 'text', 
        editable: true,
        width: 'w-24'
      },
      { 
        key: 'provenance_zoning', 
        label: 'Zonage', 
        type: 'text', 
        editable: true,
        width: 'w-32'
      },
      { 
        key: 'provenance_floor', 
        label: 'Étage', 
        type: 'text', 
        editable: true,
        width: 'w-24'
      },
      { 
        key: 'surface_state', 
        label: 'État surface', 
        type: 'text', 
        editable: true,
        width: 'w-32'
      },
      { 
        key: 'me_link', 
        label: 'Lien', 
        type: 'text', 
        editable: true,
        width: 'w-48'
      },
      { 
        key: 'material_notes', 
        label: 'Notes matériau', 
        type: 'textarea', 
        editable: true,
        width: 'w-64'
      },
      { 
        key: 'scale', 
        label: 'Échelle', 
        type: 'text', 
        editable: true,
        width: 'w-24'
      },
      { 
        key: 'me_owner', 
        label: 'Propriétaire', 
        type: 'text', 
        editable: true,
        width: 'w-40'
      },
      { 
        key: 'provenance_orientation', 
        label: 'Orientation', 
        type: 'text', 
        editable: true,
        width: 'w-32'
      },
      { 
        key: 'provenance_nomenclature', 
        label: 'Nomenclature', 
        type: 'text', 
        editable: true,
        width: 'w-40'
      },
      { 
        key: 'is_public', 
        label: 'Public', 
        type: 'checkbox', 
        editable: true,
        sortable: true,
        width: 'w-20'
      },
      { 
        key: 'created_at', 
        label: 'Créé le', 
        type: 'date', 
        sortable: true,
        editable: false,
        width: 'w-40'
      },
      { 
        key: 'updated_at', 
        label: 'Modifié le', 
        type: 'date', 
        sortable: true,
        editable: false,
        width: 'w-40'
      }
    ]
  },

  locations: {
    title: 'Localisations',
    entityType: 'locations',
    detailRoute: '/location',
    backRoute: '/admin',
    exportFilename: 'localisations',
    searchableFields: ['site', 'address', 'place', 'country', 'geoname_id'],
    requiredFields: ['user_id', 'site'],
    columns: [
      { 
        key: 'user_id', 
        label: 'Utilisateur', 
        type: 'select', 
        editable: true, 
        sortable: true, 
        required: true,
        width: 'w-40'
      },
      { 
        key: 'site', 
        label: 'Site', 
        type: 'text', 
        editable: true, 
        sortable: true,
        required: true,
        searchable: true,
        width: 'w-48'
      },
      { 
        key: 'address', 
        label: 'Adresse', 
        type: 'text', 
        editable: true,
        searchable: true,
        width: 'w-64'
      },
      { 
        key: 'post_code', 
        label: 'Code postal', 
        type: 'text', 
        editable: true,
        sortable: true,
        width: 'w-32'
      },
      { 
        key: 'place', 
        label: 'Lieu', 
        type: 'text', 
        editable: true,
        sortable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'country', 
        label: 'Pays', 
        type: 'text', 
        editable: true,
        sortable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'latitude', 
        label: 'Latitude', 
        type: 'number', 
        editable: true,
        width: 'w-32'
      },
      { 
        key: 'longitude', 
        label: 'Longitude', 
        type: 'number', 
        editable: true,
        width: 'w-32'
      },
      { 
        key: 'geoname_id', 
        label: 'Geoname ID', 
        type: 'text', 
        editable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'is_public', 
        label: 'Public', 
        type: 'checkbox', 
        editable: true,
        sortable: true,
        width: 'w-20'
      },
      { 
        key: 'is_confidential', 
        label: 'Confidentiel', 
        type: 'checkbox', 
        editable: true,
        sortable: true,
        width: 'w-24',
        formatter: (value: unknown): string => value ? '🔒 Oui' : 'Non'
      },
      { 
        key: 'created_at', 
        label: 'Créé le', 
        type: 'date', 
        sortable: true,
        editable: false,
        width: 'w-40'
      },
      { 
        key: 'updated_at', 
        label: 'Modifié le', 
        type: 'date', 
        sortable: true,
        editable: false,
        width: 'w-40'
      }
    ]
  },

  projects: {
    title: 'Projets',
    entityType: 'projects',
    detailRoute: '/project',
    backRoute: '/admin',
    exportFilename: 'projets',
    searchableFields: ['title', 'description', 'type', 'acronym', 'aurehal_id'],
    requiredFields: ['user_id', 'title'],
    columns: [
      { 
        key: 'user_id', 
        label: 'Utilisateur', 
        type: 'select', 
        editable: true, 
        sortable: true, 
        required: true,
        width: 'w-40'
      },
      { 
        key: 'title', 
        label: 'Titre', 
        type: 'text', 
        editable: true, 
        sortable: true,
        required: true,
        searchable: true,
        width: 'w-48'
      },
      { 
        key: 'type', 
        label: 'Type', 
        type: 'text', 
        editable: true,
        sortable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'acronym', 
        label: 'Acronyme', 
        type: 'text', 
        editable: true,
        sortable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'aurehal_id', 
        label: 'Aurehal ID', 
        type: 'text', 
        editable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'description', 
        label: 'Description', 
        type: 'textarea', 
        editable: true,
        searchable: true,
        width: 'w-64'
      },
      { 
        key: 'fundedby', 
        label: 'Financé par', 
        type: 'text', 
        editable: true,
        width: 'w-40'
      },
      { 
        key: 'link', 
        label: 'Lien de référence', 
        type: 'text', 
        editable: true,
        width: 'w-48'
      },
      { 
        key: 'is_public', 
        label: 'Public', 
        type: 'checkbox', 
        editable: true,
        sortable: true,
        width: 'w-20'
      },
      { 
        key: 'created_at', 
        label: 'Créé le', 
        type: 'date', 
        sortable: true,
        editable: false,
        width: 'w-40'
      },
      { 
        key: 'updated_at', 
        label: 'Modifié le', 
        type: 'date', 
        sortable: true,
        editable: false,
        width: 'w-40'
      }
    ]
  },

  memos: {
    title: 'Mémos',
    entityType: 'memos',
    detailRoute: '/memos',
    backRoute: '/admin',
    exportFilename: 'memos',
    searchableFields: ['title', 'description', 'user_id', 'uuid'],
    requiredFields: ['user_id', 'title'],
    columns: [
      { 
        key: 'uuid', 
        label: 'UUID', 
        type: 'readonly',
        sortable: true,
        searchable: true,
        width: 'w-48'
      },
      { 
        key: 'user_id', 
        label: 'Utilisateur', 
        type: 'select', 
        editable: true, 
        sortable: true, 
        required: true,
        searchable: false,
        width: 'w-40'
      },
      { 
        key: 'triid', 
        label: 'TRI ID', 
        type: 'text', 
        editable: true, 
        sortable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'title', 
        label: 'Titre', 
        type: 'text', 
        editable: true, 
        sortable: true,
        required: true,
        searchable: true,
        width: 'w-48'
      },
      { 
        key: 'type', 
        label: 'Type', 
        type: 'text', 
        editable: true,
        sortable: true,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'is_model', 
        label: 'Modèle', 
        type: 'checkbox', 
        editable: true,
        sortable: true,
        width: 'w-24'
      },
      { 
        key: 'is_draft', 
        label: 'Brouillon', 
        type: 'checkbox', 
        editable: true,
        sortable: true,
        width: 'w-24'
      },
      { 
        key: 'start_time', 
        label: 'Début', 
        type: 'date', 
        editable: true,
        sortable: true,
        width: 'w-40'
      },
      { 
        key: 'end_time', 
        label: 'Fin', 
        type: 'date', 
        editable: true,
        sortable: true,
        width: 'w-40'
      },
      { 
        key: 'is_public', 
        label: 'Public', 
        type: 'checkbox', 
        editable: true,
        sortable: true,
        width: 'w-24'
      },
      {
        key: 'is_deleted',
        label: 'Supprimé',
        type: 'checkbox',
        editable: false,
        sortable: true,
        width: 'w-24',
        formatter: (value: unknown): string => value ? 'Oui' : 'Non'
      },
      { 
        key: 'created_at', 
        label: 'Créé le', 
        type: 'date', 
        sortable: true,
        editable: false,
        width: 'w-40'
      },
      { 
        key: 'updated_at', 
        label: 'Modifié le', 
        type: 'date', 
        sortable: true,
        editable: false,
        width: 'w-40'
      }
    ]
  },

  users: {
    title: 'Utilisateurs',
    entityType: 'users',
    detailRoute: '/user',
    backRoute: '/admin',
    exportFilename: 'utilisateurs',
    searchableFields: ['name', 'email', 'fonction', 'affiliation'],
    requiredFields: ['name', 'email'],
    columns: [
      { 
        key: 'uuid', 
        label: 'UUID', 
        type: 'text', 
        editable: false, 
        sortable: true,
        required: false,
        searchable: true,
        width: 'w-32'
      },
      { 
        key: 'name', 
        label: 'Nom', 
        type: 'text', 
        editable: true, 
        sortable: true,
        required: true,
        searchable: true,
        width: 'w-48'
      },
      { 
        key: 'email', 
        label: 'Email', 
        type: 'text', 
        editable: false, 
        sortable: true,
        required: true,
        searchable: true,
        width: 'w-64'
      },
      { 
        key: 'password', 
        label: 'Mot de passe (optionnel)', 
        type: 'text', 
        editable: true, 
        sortable: false,
        required: false,
        searchable: false,
        width: 'w-48',
        formatter: (value: unknown): string => {
          if (!value || (typeof value === 'string' && !value.trim())) {
            return 'Généré automatiquement';
          }
          return '***' + (value as string).slice(-4);
        }
      },
      { 
        key: 'role', 
        label: 'Rôle', 
        type: 'select', 
        editable: true,
        sortable: true,
        searchable: false,
        width: 'w-32',
        options: [
          { value: 'superadmin', label: 'Super Administrateur' },
          { value: 'admin', label: 'Administrateur' },
          { value: 'user', label: 'Utilisateur' },
          { value: 'visiteur', label: 'Visiteur' }
        ],
        formatter: (value: unknown): string => {
          const roleMap: Record<string, string> = {
            'superadmin': 'Super Administrateur',
            'admin': 'Administrateur',
            'user': 'Utilisateur', 
            'visiteur': 'Visiteur'
          };
          return roleMap[value as string] || value as string;
        }
      },
      { 
        key: 'fonction', 
        label: 'Fonction', 
        type: 'text', 
        editable: true,
        searchable: true,
        width: 'w-40'
      },
      { 
        key: 'affiliation', 
        label: 'Affiliation', 
        type: 'text', 
        editable: true,
        searchable: true,
        width: 'w-48'
      },
      { 
        key: 'email_verified', 
        label: 'Email vérifié', 
        type: 'checkbox', 
        editable: false,
        superadminOnly: true,
        sortable: true,
        width: 'w-32',
        formatter: (value: unknown): string => value ? 'Oui' : 'Non'
      },
      { 
        key: 'is_public', 
        label: 'Public', 
        type: 'checkbox', 
        editable: true,
        sortable: true,
        width: 'w-20'
      },
      { 
        key: 'created_at', 
        label: 'Créé le', 
        type: 'date', 
        sortable: true,
        editable: false,
        width: 'w-40'
      },
      { 
        key: 'updated_at', 
        label: 'Modifié le', 
        type: 'date', 
        sortable: true,
        editable: false,
        width: 'w-40'
      }
    ]
  }
};

// Types pour l'export
export type AssetType = Agent | DigitalEntity | MaterialEntity | Location | Project | Memo | User;
export type AssetEntityType = keyof typeof ADMIN_ASSET_CONFIGS; 